import moment from "moment-timezone";
import { formatDate } from "utils/date";

export const TASK_TYPES = [
  { type: "newEnquiryFollowup", label: "New enquiry", title: "Reply to the enquiry", action: "Quick message", kind: "message", stages: ["new", "open_enquiries"] },
  { type: "newBookingMissingDetails", label: "Missing details", title: "Request missing booking details", action: "Quick message", kind: "message", stages: ["new", "open_enquiries"] },
  { type: "newBookingFollowup", label: "New booking", title: "Prepare boat options or a quote", action: "Prepare options", kind: "options", stages: ["new"] },
  { type: "optionsSentNotSeen", label: "Options not seen", title: "Follow up on unseen options", action: "Quick message", kind: "message", stages: ["options_sent"] },
  { type: "optionsSeenNoQuote", label: "Options seen", title: "Follow up on the boat selection", action: "Quick message", kind: "message", stages: ["options_seen"] },
  { type: "quoteSentNotSeen", label: "Quote not seen", title: "Follow up on the unseen quote", action: "Quick message", kind: "message", stages: ["quote_not_seen"] },
  { type: "quoteSeenNoPayment", label: "Quote seen", title: "Follow up on payment", action: "Quick message", kind: "message", stages: ["quote_seen"] },
  { type: "quoteExpired", label: "Quote expired", title: "Follow up on the expired quote", action: "Quick message", kind: "message", stages: ["quote_expired"] },
];

const STAGE_DEFAULTS = [
  ["new", "New", "A booking without options, quotes, availability progress or notes."],
  ["open_enquiries", "Open enquiries", "Enquiries that are no longer new and have no recorded payment."],
  ["options_sent", "Options sent, not seen", "Options sent to the customer and not all viewed."],
  ["options_seen", "Options seen, not quoted", "Options viewed, with no booking marked available."],
  ["quote_not_seen", "Quote not seen", "At least one main quote has not been viewed or paid."],
  ["quote_seen", "Quote seen, not paid", "A main quote has been viewed and has no payment."],
  ["quote_with_payments", "Quote with payments", "A main quote has a deposit or full payment."],
  ["quote_expired", "Quote expired", "All main quotes have expired with an outstanding amount."],
  ["late_payments", "Late payments", "A partly paid booking within two weeks of its cruise date."],
  ["up_coming", "Upcoming", "Bookings with a recorded main-quote payment."],
];

const CARD_STAGE_ORDER = ["late_payments", "quote_expired", "quote_with_payments", "quote_seen", "quote_not_seen", "options_seen", "options_sent", "new", "open_enquiries"];
const DRAFT_TASK_TYPES = ["newEnquiryFollowup", "optionsSentNotSeen", "optionsSeenNoQuote", "quoteSentNotSeen", "quoteSeenNoPayment", "quoteExpired"];

export const SORTS = [
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["cruise-soon", "Cruise date soonest first"],
  ["cruise-late", "Cruise date latest first"],
  ["lead", "By lead"],
  ["unread", "New message"],
];

const STATUS_LABELS = {
  UNSPECIFIED: "Unspecified",
  OptionsSent: "Options sent",
  OptionsSeen: "Options seen",
  QuoteSent: "Quote sent",
  QuoteSeenNotPaid: "Quote seen, not paid",
  QuoteExired: "Quote expired",
  LatePayment: "Late payment",
};

const descriptionCache = new Map();

export function parseTime(value) {
  const parsed = value == null ? NaN : moment(value, moment.ISO_8601, true).valueOf();
  return Number.isFinite(parsed) ? parsed : null;
}

export function leadName(lead) {
  const parts = [lead?.firstName, lead?.lastName].filter((value) => value != null && String(value).trim() !== "");
  return parts.length ? parts.join(" ") : null;
}

export function bookingAge(createdAt, now) {
  const created = parseTime(createdAt);
  if (created == null) return null;
  const minutes = Math.max(0, Math.floor((now - created) / 60000));
  const parts = [
    [Math.floor(minutes / 1440), "day"],
    [Math.floor((minutes % 1440) / 60), "hour"],
    [minutes % 60, "minute"],
  ].filter(([value]) => value > 0).map(([value, unit]) => `${value} ${unit}${value === 1 ? "" : "s"}`);
  return parts.length ? `${parts.join(", ")} ago` : "Just now";
}

export function cruiseDay(value) {
  return parseTime(value) == null ? null : moment(value).format("YYYY-MM-DD");
}

export function cruiseTime(value) {
  return parseTime(value) == null ? null : moment(value).format("HH:mm");
}

export function lastAction(booking) {
  if (booking.lastStatus == null) return null;
  const label = STATUS_LABELS[booking.lastStatus] || String(booking.lastStatus).replace(/([a-z])([A-Z])/g, "$1 $2");
  const when = parseTime(booking.lastStatusSetOn) == null ? null : moment(booking.lastStatusSetOn).format("D MMM YYYY h:mma");
  return label && when ? `${label} · ${when}` : label;
}

function uniqueNames(values) {
  return [...new Set(values.filter((value) => value != null && value !== ""))];
}

export function packageNames(booking) {
  return uniqueNames((booking.items || []).map((item) => item.boatPackage?.name));
}

export function quotedBoatNames(booking) {
  return uniqueNames((booking.items || []).filter((item) => item.quoteItems?.length).map((item) => item.boat?.name));
}

export function subjectName(entry) {
  return entry?.lead ? leadName(entry.lead) : (entry?.name || null);
}

export function subjectEmail(entry) {
  return entry?.lead ? entry.lead.email : entry?.email;
}

export function subjectPhone(entry) {
  return entry?.lead ? entry.lead.phone : entry?.phone;
}

export function subjectLeadId(entry) {
  return entry?.lead?.id || entry?.leadId || null;
}

export function subjectBookingId(entry) {
  return entry?.reference ? entry.id : null;
}

export function subjectReference(entry) {
  return entry?.reference || null;
}

export function stageDefinitions(payload) {
  const overrides = new Map((payload?.stages || []).map((stage) => [stage.key, stage]));
  return STAGE_DEFAULTS.map(([key, label, tooltip]) => {
    const override = overrides.get(key);
    return { key, label: override?.label || label, tooltip: override?.tooltip || tooltip };
  });
}

export function taskSpec(type) {
  return TASK_TYPES.find((task) => task.type === type);
}

export function currentStage(booking, stagesByBooking) {
  const stages = stagesByBooking.get(booking.id) || [];
  return CARD_STAGE_ORDER.find((stage) => stages.includes(stage));
}

export function stageLabel(booking, stages, stagesByBooking) {
  const key = currentStage(booking, stagesByBooking);
  const quotes = (booking.quotes || []).filter((quote) => quote.type !== "additional");
  if (key === "quote_not_seen" && quotes.length && quotes.every((quote) => !quote.sentOn && !quote.seenOn)) return "Quote not sent";
  if (key === "open_enquiries" && booking.options?.length && booking.options.every((option) => !option.sentOn && !option.seenOn)) return "Options not sent";
  return stages.find((stage) => stage.key === key)?.label || "Booking";
}

export function latestTask(booking, tasksByBooking, stagesByBooking) {
  const task = tasksByBooking.get(booking.id);
  const spec = taskSpec(task?.type);
  return task && task.status === "pending" && spec?.stages.includes(currentStage(booking, stagesByBooking)) ? task : null;
}

export function taskState(booking, tasksByBooking, stagesByBooking) {
  const task = tasksByBooking.get(booking.id);
  if (task?.status === "done") return "done";
  return latestTask(booking, tasksByBooking, stagesByBooking) ? "pending" : "none";
}

export function taskCategories(bookings, tasksByBooking, stagesByBooking, leadTasks) {
  const counts = new Map();
  bookings.forEach((booking) => {
    const type = latestTask(booking, tasksByBooking, stagesByBooking)?.type;
    if (type) counts.set(type, (counts.get(type) || 0) + 1);
  });
  const ordered = [...TASK_TYPES.filter((task) => task.type !== "newEnquiryFollowup"), ...TASK_TYPES.filter((task) => task.type === "newEnquiryFollowup")];
  return ordered.map((task) => ({
    ...task,
    count: (counts.get(task.type) || 0) + (task.type === "newEnquiryFollowup" ? leadTasks.length : 0),
  })).filter((task) => task.count > 0);
}

export function unreadSummary(booking) {
  const unread = (booking.messages || []).filter((message) => (message.lead || message.owner) && !message.seenOn);
  const times = unread.map((message) => parseTime(message.createdAt)).filter((value) => value != null);
  return {
    count: unread.length,
    last: times.length ? new Date(Math.max(...times)).toISOString() : null,
  };
}

export function sumUnread(bookings, unreadByBooking) {
  return bookings.reduce((total, booking) => total + (unreadByBooking.get(booking.id)?.count || 0), 0);
}

export function formatWhen(value, withTime = false) {
  return formatDate(value, withTime) || "Unavailable";
}

export function friendlyEnum(value) {
  return value
    ? String(value).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").toLowerCase().replace(/^./, (character) => character.toUpperCase())
    : "Unavailable";
}

export function cleanDescription(value) {
  const source = value || "";
  if (typeof source === "string" && !/[<&]/.test(source)) {
    return source.replace(/\r\n?/g, "\n").replace(/^[\t\n\f ]+/, "").replace(/\u0000/g, "");
  }
  if (descriptionCache.has(source)) {
    const cached = descriptionCache.get(source);
    descriptionCache.delete(source);
    descriptionCache.set(source, cached);
    return cached;
  }
  const document = new DOMParser().parseFromString(source, "text/html");
  document.querySelectorAll("br").forEach((node) => node.replaceWith("\n"));
  const result = document.body.textContent || "";
  descriptionCache.set(source, result);
  if (descriptionCache.size > 2048) descriptionCache.delete(descriptionCache.keys().next().value);
  return result;
}

export function storedTaskMessage(task) {
  if (!task || !DRAFT_TASK_TYPES.includes(task.type)) return "";
  return cleanDescription(task.description).replace(/I tried to call you a moment ago,\s*/gi, "").replace(/\u2014/g, "-").trim();
}

export function messageTaskId(task) {
  return taskSpec(task?.type)?.kind === "message" ? task.id : null;
}

export function groupedBoats(booking) {
  const groups = [];
  const byBoat = new Map();
  (booking.items || []).forEach((item) => {
    if (!item.boat) return;
    let group = byBoat.get(item.boat.id);
    if (!group) {
      group = {
        id: item.boat.id,
        name: item.boat.name,
        skipperRequired: item.boat.skipperRequired,
        skippers: item.skippers || [],
        deckies: item.deckies || [],
        itemIds: [],
      };
      groups.push(group);
      byBoat.set(item.boat.id, group);
    }
    if (item.id && !group.itemIds.includes(item.id)) group.itemIds.push(item.id);
  });
  return groups;
}

export function boatNames(booking) {
  return groupedBoats(booking).map((boat) => boat.name).join(", ");
}

export function selectedItemIds(booking, selectedByBooking) {
  const ids = (booking.items || []).filter((item) => item.boat).map((item) => item.id).filter(Boolean);
  const selected = selectedByBooking.get(booking.id);
  return selected ? ids.filter((id) => selected.has(id)) : ids;
}

export function toggleBoatGroup(booking, itemId, checked, selectedByBooking) {
  const group = groupedBoats(booking).find((boat) => boat.itemIds.includes(itemId));
  if (!group) return false;
  const selected = new Set(selectedItemIds(booking, selectedByBooking));
  group.itemIds.forEach((id) => (checked ? selected.add(id) : selected.delete(id)));
  selectedByBooking.set(booking.id, selected);
  return true;
}

export function savedBoatChecks(booking, tasksByBooking) {
  const task = tasksByBooking.get(booking.id);
  if (task?.type !== "newBookingFollowup" || !task.meta) return { task, checks: [], error: false };
  try {
    const parsed = JSON.parse(task.meta);
    return Array.isArray(parsed) ? { task, checks: parsed, error: false } : { task, checks: [], error: true };
  } catch (error) {
    return { task, checks: [], error: true };
  }
}

export function boatCheckFor(group, checks) {
  return checks.find((check) => (group.id ? check.boatId === group.id : check.boatName === group.name));
}

export function boatCheckLabel(check) {
  if (!check) return { label: "Not checked", tone: "unknown" };
  const available = check.hasCalendar === true && check.isAvailableCalendar === true && check.isAvailableBookings === true;
  const conflict = (check.isAvailableCalendar === false && check.hasCalendar === true) || check.isAvailableBookings === false;
  if (available) return { label: "Last check: available", tone: "available" };
  if (conflict) return { label: "Last check: conflict", tone: "conflict" };
  return { label: "Needs confirmation", tone: "unknown" };
}

export function availabilitySummary(check) {
  if (check.hasCalendar && check.isAvailableCalendar && check.isAvailableBookings) return "Available";
  if (check.isAvailableBookings === false || (check.hasCalendar && check.isAvailableCalendar === false)) return "Unavailable";
  return "Needs confirmation";
}

export function recordMatches(booking, query) {
  const lead = booking.lead || {};
  return [
    booking.reference, leadName(lead), lead.email, lead.phone, cruiseDay(booking.cruiseDate),
    ...(booking.quotes || []).map((quote) => quote.reference),
    ...(booking.items || []).map((item) => item.boat?.name),
  ].join(" ").toLowerCase().includes(query);
}

function compareBookings(sort, left, right, unreadByBooking) {
  let difference = 0;
  switch (sort) {
    case "oldest":
      difference = String(left.createdAt || "").localeCompare(String(right.createdAt || ""));
      break;
    case "cruise-soon":
      difference = String(left.cruiseDate || "").localeCompare(String(right.cruiseDate || ""));
      break;
    case "cruise-late":
      difference = String(right.cruiseDate || "").localeCompare(String(left.cruiseDate || ""));
      break;
    case "lead":
      difference = left.lead?.id && right.lead?.id ? left.lead.id.localeCompare(right.lead.id) : 0;
      break;
    case "unread": {
      const leftAt = unreadByBooking.get(left.id)?.last;
      const rightAt = unreadByBooking.get(right.id)?.last;
      difference = leftAt && rightAt ? rightAt.localeCompare(leftAt) : leftAt ? -1 : rightAt ? 1 : 0;
      break;
    }
    default:
      difference = String(right.createdAt || "").localeCompare(String(left.createdAt || ""));
  }
  return difference || String(left.reference).localeCompare(String(right.reference));
}

export function filterRecords(bookings, { stage, task, query, searchInStage, sort, stagesByBooking, tasksByBooking, unreadByBooking }) {
  let rows = bookings.filter((booking) => (!searchInStage && query) || !stage || stagesByBooking.get(booking.id)?.includes(stage));
  if (task) rows = rows.filter((booking) => latestTask(booking, tasksByBooking, stagesByBooking)?.type === task);
  if (query) rows = rows.filter((booking) => recordMatches(booking, query));
  return rows.slice().sort((left, right) => compareBookings(sort, left, right, unreadByBooking));
}

export function sortHint(bookings, sort, unreadByBooking) {
  if (sort === "lead" && !bookings.every((booking) => booking.lead?.id)) return "Lead order is unavailable.";
  if (sort === "unread" && ![...unreadByBooking.values()].some((entry) => entry.last)) return "Message ordering is unavailable.";
  return "";
}

export function listOrUnavailable(values) {
  return Array.isArray(values) ? values.join(", ") || "None" : "Unavailable";
}

export function sortMessages(messages) {
  if (!Array.isArray(messages)) throw new Error("Conversation could not be loaded.");
  return messages.slice().sort((left, right) => String(left.createdAt).localeCompare(String(right.createdAt)) || String(left.id).localeCompare(String(right.id)));
}

export function contactName(contact) {
  return [contact?.firstName, contact?.lastName].filter(Boolean).join(" ") || contact?.email || "";
}
